import { useCallback, useEffect, useRef } from 'react'

// ============================================================================
// TAP vs HOLD, off one pointer sequence.
//
// Two controls in this app need it and they must behave identically: the capture
// button in the tab bar ("Tap opens the capture sheet. Long-press starts a voice
// memo and records for as long as the finger is down") and the 56px record
// circle in the composer ("Hold to record"). Two hand-rolled copies of a
// press-timer is how one of them ends up 250ms and the other 500ms.
//
// THE INVARIANT: exactly one of `onTap` and `onHold` fires per gesture, never
// both and never neither. And a hold that started ALWAYS gets its `onRelease`,
// including when the browser cancels the pointer mid-gesture (a scroll claimed
// it, a system sheet came up, the finger slid off) — an open microphone
// surviving a cancelled gesture is the one failure this must not have.
// ============================================================================

/** How long the finger stays down before it is a hold and not a tap. */
export const HOLD_MS = 400

export interface HoldHandlers {
  onPointerDown: () => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onPointerLeave: () => void
}

export function useHold({
  onTap,
  onHold,
  onRelease,
  ms = HOLD_MS,
}: {
  onTap: () => void
  onHold: () => void
  onRelease: () => void
  ms?: number
}): HoldHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holding = useRef(false)

  // The callbacks ride on a ref so the handlers below never change identity —
  // a re-created onPointerDown between the press and the release is how a hold
  // loses the closure that knows it was a hold.
  const latest = useRef({ onTap, onHold, onRelease })
  latest.current = { onTap, onHold, onRelease }

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])

  // Unmounting mid-gesture must not leave a timer that fires into a dead tree.
  useEffect(() => clear, [clear])

  const onPointerDown = useCallback(() => {
    holding.current = false
    clear()
    timer.current = setTimeout(() => {
      timer.current = null
      holding.current = true
      latest.current.onHold()
    }, ms)
  }, [clear, ms])

  const onPointerUp = useCallback(() => {
    clear()
    if (holding.current) {
      holding.current = false
      latest.current.onRelease()
      return
    }
    latest.current.onTap()
  }, [clear])

  const onCancel = useCallback(() => {
    clear()
    if (holding.current) {
      holding.current = false
      latest.current.onRelease()
    }
  }, [clear])

  return {
    onPointerDown,
    onPointerUp,
    onPointerCancel: onCancel,
    onPointerLeave: onCancel,
  }
}
